import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import Header from './components/Header';
import MetricsCards from './components/MetricsCards';
import InsightCard from './components/InsightCard';
import PromptDocsButton from './components/PromptDocsButton';
import { useInsightsData, useInventoryDates, useInventoryUsers } from './hooks';
import { Insight } from './types/insights';
import { NotificationContainer, useNotifications } from './components/NotificationToast';
import LoginScreen from './components/LoginScreen';
import { DEMO_CREDENTIALS, DemoCredential } from './data/demoCredentials';
import { formatInventoryDate } from './utils/date';

const getStoredAuthUser = (): DemoCredential | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.localStorage.getItem('demo-auth-user');
    return stored ? (JSON.parse(stored) as DemoCredential) : null;
  } catch (error) {
    console.warn('Failed to read stored auth user:', error);
    return null;
  }
};

function App() {
  const [authenticatedUser, setAuthenticatedUser] = useState<DemoCredential | null>(() => getStoredAuthUser());
  const [selectedUserId, setSelectedUserId] = useState(() => authenticatedUser?.userId || '');
  const [selectedDate, setSelectedDate] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [categoryProgress, setCategoryProgress] = useState<Record<string, number>>({});
  
  // Add notifications
  const { notifications, dismissNotification } = useNotifications();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (authenticatedUser) {
      window.localStorage.setItem('demo-auth-user', JSON.stringify(authenticatedUser));
      setSelectedUserId((prev) => prev || authenticatedUser.userId);
    } else {
      window.localStorage.removeItem('demo-auth-user');
      setSelectedUserId('');
    }
  }, [authenticatedUser]);

  const handleLoginSuccess = useCallback((user: DemoCredential) => {
    setAuthenticatedUser(user);
    setSelectedUserId(user.userId);
  }, []);

  const handleLogout = () => {
    setAuthenticatedUser(null);
    setSelectedUserId('');
    setSelectedDate('');
  };

  const isAuthenticated = Boolean(authenticatedUser);

  const {
    dates: availableDates,
    isLoading: isLoadingDates,
    error: datesError,
    generatedAt: datesGeneratedAt,
  } = useInventoryDates();

  const {
    entries: availableUsers,
    isLoading: isLoadingUsers,
    error: usersError,
    generatedAt: usersGeneratedAt,
    count: availableUserCount,
  } = useInventoryUsers(selectedDate || null);

  useEffect(() => {
    if (!selectedDate && availableDates && availableDates.length > 0) {
      setSelectedDate(availableDates[0].date);
    }
  }, [availableDates, selectedDate]);

  useEffect(() => {
    if (!selectedUserId && availableUsers.length > 0) {
      setSelectedUserId(availableUsers[0].user_id);
      return;
    }

    if (
      selectedUserId &&
      availableUsers.length > 0 &&
      !availableUsers.some((entry) => entry.user_id === selectedUserId)
    ) {
      setSelectedUserId(availableUsers[0].user_id);
    }
  }, [availableUsers, selectedUserId]);

  // Use TanStack Query hook for insights data
  const { 
    insights: insightsData, 
    isLoading: loading, 
    isFetching,
    error,
    refreshInsights
  } = useInsightsData({
    userId: selectedUserId,
    date: selectedDate,
    enabled: Boolean(isAuthenticated && selectedUserId && selectedDate),
  });

  const groupedInsights = useMemo(() => {
    const map: Record<string, Insight[]> = {};
    const order: string[] = [];
    (insightsData?.insights ?? []).forEach((insight) => {
      const category = insight.category || 'General';
      if (!map[category]) {
        map[category] = [];
        order.push(category);
      }
      map[category].push(insight);
    });
    return { map, order };
  }, [insightsData?.insights]);

  useEffect(() => {
    if (groupedInsights.order.length === 0) {
      setCategoryProgress({});
      setExpandedCategories(new Set());
      return;
    }

    const initial: Record<string, number> = {};
    groupedInsights.order.forEach((category) => {
      initial[category] = 0;
    });
    setCategoryProgress(initial);
    setExpandedCategories(new Set([groupedInsights.order[0]]));
  }, [groupedInsights.order]);

  const toggleInsightCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleCardDismiss = useCallback(
    (category: string) => {
      const currentIndex = categoryProgress[category] ?? 0;
      const nextIndex = currentIndex + 1;

      setCategoryProgress((prev) => ({
        ...prev,
        [category]: nextIndex,
      }));

    },
    [categoryProgress]
  );

  const handleCategoryRestart = useCallback((category: string) => {
    setCategoryProgress((prev) => ({
      ...prev,
      [category]: 0,
    }));
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      next.add(category);
      return next;
    });
  }, []);

  const formatDate = (date: string) => formatInventoryDate(date);

  if (!isAuthenticated) {
    return (
      <div className="App">
        <LoginScreen users={DEMO_CREDENTIALS} onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="auth-status-bar">
        <div>
          <p className="auth-status-bar__label">Logged in as</p>
          <p className="auth-status-bar__user">{authenticatedUser?.displayName}</p>
          <p className="auth-status-bar__email">{authenticatedUser?.email}</p>
        </div>
        <button className="logout-button" onClick={handleLogout}>
          Log Out
        </button>
      </div>
      <Header
        selectedUserId={selectedUserId}
        selectedDate={selectedDate}
        onUserChange={setSelectedUserId}
        onDateChange={setSelectedDate}
        userName={insightsData?.user_name}
        formattedDate={selectedDate ? formatDate(selectedDate) : undefined}
        onInsightsReload={refreshInsights}
        availableDates={availableDates}
        availableUsers={availableUsers}
        isLoadingDates={isLoadingDates}
        isLoadingUsers={isLoadingUsers}
        datesError={datesError instanceof Error ? datesError.message : undefined}
        usersError={usersError instanceof Error ? usersError.message : undefined}
        inventoryMeta={{
          datesGeneratedAt,
          usersGeneratedAt,
          userCount: availableUserCount,
        }}
      />

      <main className="main-content">
        {/* Prompt Engineering - Primary feature at top */}
        {selectedUserId && selectedDate ? (
          <PromptDocsButton 
            selectedUserId={selectedUserId}
            selectedDate={selectedDate}
          />
        ) : (
          <div className="loading">Pick a date and user to load prompt tools.</div>
        )}
        
        {loading && <div className="loading">Loading insights...</div>}
        
        {/* Show error only if there's no data and we have an error */}
        {error && !insightsData && <div className="error">{
          error?.message || 'Failed to load insights. Please try again.'
        }</div>}
        
        {/* Show background fetching indicator */}
        {isFetching && !loading && insightsData && (
          <div className="loading-overlay">
            <div className="loading-indicator">
              Refreshing data for {formatDate(selectedDate)}...
            </div>
          </div>
        )}
        
        {/* Show insights cards when we have data, even during background refresh */}
        {insightsData && !loading && (
          <>
            <MetricsCards metrics={insightsData.metrics} />
            
            <div className="insights-container">
              {groupedInsights.order.length > 0 ? (
                groupedInsights.order.map((category) => {
                  const insightsForCategory = groupedInsights.map[category] || [];
                  if (insightsForCategory.length === 0) return null;

                  const visibleIndex = categoryProgress[category] ?? 0;
                  const currentInsight = insightsForCategory[visibleIndex];

                  if (!currentInsight) {
                    const fallbackInsight = insightsForCategory[0];
                    return (
                      <div
                        key={`${category}-reset`}
                        className={`insight-card ${expandedCategories.has(category) ? 'expanded' : ''}`}
                      >
                        <div className="insight-header" onClick={() => toggleInsightCategory(category)}>
                          <div className="category-info">
                            <span className="category-icon">{fallbackInsight?.category_icon || '📊'}</span>
                            <span className="category-name">{category}</span>
                            <span className="priority-tag">{fallbackInsight?.priority_tag || 'insight'}</span>
                          </div>
                        </div>
                        {expandedCategories.has(category) && (
                          <div className="insight-content reset-content">
                            <div className="main-insight">
                              You're all caught up on {category} insights.
                            </div>
                            <div className="recommendation">
                              Want to revisit them? Start over to cycle through the set again.
                            </div>
                            <button
                              className="action-btn primary"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCategoryRestart(category);
                              }}
                            >
                              Start Over
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <InsightCard
                      key={`${category}-${visibleIndex}`}
                      insight={currentInsight}
                      isExpanded={expandedCategories.has(category)}
                      onToggle={() => toggleInsightCategory(category)}
                      onClose={() => handleCardDismiss(category)}
                    />
                  );
                })
              ) : (
                <div className="no-insights">
                  No insights available for this date. Try running the pipeline first.
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Notification Container */}
      <NotificationContainer
        notifications={notifications}
        onDismiss={dismissNotification}
        position="top-right"
      />
    </div>
  );
}

export default App;
